import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { auth } from '@/src/lib/auth'
import { isStaffSession, LOGIN_PATH } from '@/src/lib/userAccess'
import { AdminShell, AdminSpinner } from '@/src/components/admin/AdminPrimitives'
import type { Session } from '@supabase/supabase-js'

interface ProtectedStaffRouteProps {
  children: React.ReactNode
}

/** Admin único del sitio (is_admin / app_settings). */
export default function ProtectedStaffRoute({ children }: ProtectedStaffRouteProps) {
  const [session, setSession] = useState<Session | null>(null)
  const [isStaff, setIsStaff] = useState(false)
  const [loading, setLoading] = useState(true)
  const location = useLocation()

  useEffect(() => {
    let cancelled = false

    async function run() {
      const s = await auth.getSession()
      if (cancelled) return
      setSession(s)
      if (!s) {
        setLoading(false)
        return
      }
      const staff = await isStaffSession(s)
      if (cancelled) return
      setIsStaff(staff)
      setLoading(false)
    }

    run()

    const unsub = auth.onAuthStateChange(async (s) => {
      setSession(s)
      if (!s) {
        setIsStaff(false)
        setLoading(false)
        return
      }
      setIsStaff(await isStaffSession(s))
      setLoading(false)
    })

    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  if (loading) {
    return (
      <AdminShell className="flex items-center justify-center">
        <AdminSpinner />
      </AdminShell>
    )
  }

  if (!session) {
    return <Navigate to={LOGIN_PATH} state={{ from: location }} replace />
  }

  if (!isStaff) {
    return <Navigate to={LOGIN_PATH} replace />
  }

  return <>{children}</>
}
